import { useState } from 'react';
import { isAmplifier, type SubstanceKey, type SubstanceReason } from '@rx/shared';

/**
 * "I took something else."
 *
 * This covers the paper chart's Other / Extra Medication column, and it is quietly one
 * of the most valuable screens in the product. The precautions document lists what adds
 * to dizziness and sedation — alcohol, cannabis, sleep aids, sedating antihistamines,
 * benzodiazepines, opioids, gabapentin, muscle relaxants — and those are exactly the
 * things a person is least likely to mention unprompted in a ten-minute appointment.
 *
 * So they are offered as ordinary buttons alongside Tums, with no warning styling and
 * no friction. Making them feel like a confession is the fastest way to stop them being
 * recorded at all.
 */

const SUBSTANCES: { key: SubstanceKey; label: string }[] = [
  { key: 'ANTACID', label: 'Tums / antacid' },
  { key: 'IBUPROFEN', label: 'Ibuprofen' },
  { key: 'ACETAMINOPHEN', label: 'Tylenol' },
  { key: 'ASPIRIN', label: 'Aspirin' },
  { key: 'SEDATING_ANTIHISTAMINE', label: 'Benadryl / allergy pill' },
  { key: 'SLEEP_MED', label: 'Sleep aid' },
  { key: 'OPIOID', label: 'Pain pill' },
  { key: 'MUSCLE_RELAXANT', label: 'Muscle relaxant' },
  { key: 'BENZODIAZEPINE', label: 'Anxiety pill' },
  { key: 'GABAPENTIN', label: 'Gabapentin' },
  { key: 'ALCOHOL', label: 'Alcohol' },
  { key: 'CANNABIS', label: 'Cannabis' },
  { key: 'SUPPLEMENT', label: 'Vitamin / supplement' },
  { key: 'OTHER_RX', label: 'Another prescription' },
  { key: 'OTC_OTHER', label: 'Something else' },
];

const REASONS: { key: SubstanceReason; label: string }[] = [
  { key: 'HEADACHE', label: 'Headache' },
  { key: 'HEARTBURN', label: 'Heartburn' },
  { key: 'PAIN', label: 'Pain' },
  { key: 'COULD_NOT_SLEEP', label: "Couldn't sleep" },
  { key: 'NAUSEA', label: 'Nausea' },
  { key: 'ANXIETY', label: 'Anxiety' },
  { key: 'OTHER', label: 'Other' },
];

const AMOUNTS = [1, 2, 3];

export interface ExtraSubstanceSubmission {
  substanceKey: SubstanceKey;
  substanceName?: string;
  quantity?: number;
  reason?: SubstanceReason;
  notes?: string;
}

export function ExtraFlow({
  onSubmit,
  onCancel,
}: {
  onSubmit: (s: ExtraSubstanceSubmission) => Promise<void>;
  onCancel: () => void;
}) {
  const [substance, setSubstance] = useState<SubstanceKey | undefined>();
  const [quantity, setQuantity] = useState<number | undefined>();
  const [reason, setReason] = useState<SubstanceReason | undefined>();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const label = SUBSTANCES.find((s) => s.key === substance)?.label;
  const needsName = substance === 'OTC_OTHER' || substance === 'OTHER_RX';

  async function save(): Promise<void> {
    if (substance === undefined) return;
    setBusy(true);
    const submission: ExtraSubstanceSubmission = { substanceKey: substance };
    if (name.trim().length > 0) submission.substanceName = name.trim();
    else if (label !== undefined) submission.substanceName = label;
    if (quantity !== undefined) submission.quantity = quantity;
    if (reason !== undefined) submission.reason = reason;
    try {
      await onSubmit(submission);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="app">
      <h1>What did you take?</h1>
      <p className="muted">
        Anything at all counts — the time is recorded for you. This is worth writing down
        even if it seems small.
      </p>

      <div className="chips">
        {SUBSTANCES.map((s) => (
          <button
            key={s.key}
            type="button"
            // Deliberately identical styling for amplifiers: no red, no warning icon.
            // The flag is recorded, not displayed back as judgement.
            className={`chip ${substance === s.key ? 'on' : ''}`}
            aria-pressed={substance === s.key}
            onClick={() => {
              setSubstance(s.key);
            }}
          >
            {s.label}
          </button>
        ))}
      </div>

      {substance !== undefined && (
        <>
          {needsName && (
            <>
              <h2>What was it called?</h2>
              <input
                className="field"
                aria-label="Name of what you took"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
              />
            </>
          )}

          <h2>How much?</h2>
          <div className="chips">
            {AMOUNTS.map((n) => (
              <button
                key={n}
                type="button"
                className={`chip wide ${quantity === n ? 'on' : ''}`}
                aria-pressed={quantity === n}
                onClick={() => {
                  setQuantity(n);
                }}
              >
                {n === 3 ? '3 or more' : String(n)}
              </button>
            ))}
          </div>

          <h2>Why?</h2>
          <div className="chips">
            {REASONS.map((r) => (
              <button
                key={r.key}
                type="button"
                className={`chip ${reason === r.key ? 'on' : ''}`}
                aria-pressed={reason === r.key}
                onClick={() => {
                  setReason(r.key);
                }}
              >
                {r.label}
              </button>
            ))}
          </div>

          {/* Stated once, plainly, only for the substances that genuinely interact —
              and framed as something to tell the doctor, never as a rebuke. */}
          {isAmplifier(substance) && (
            <p className="note">
              Worth mentioning to the doctor: this can add to dizziness or sleepiness
              alongside the prescriptions.
            </p>
          )}
        </>
      )}

      <div className="btn-row">
        <button
          className="btn-primary"
          disabled={busy || substance === undefined}
          onClick={() => void save()}
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button className="btn-quiet" type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
      </div>
    </main>
  );
}
